import { useState } from "react";
import axios from "axios";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import { BRAND } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3.5 text-sm text-cream placeholder-fern focus:border-lavender/60 transition-colors";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", checkin: "", checkout: "", guests: "2", dome: "", message: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        guests: form.guests ? parseInt(form.guests, 10) : null,
        dome: form.dome || null,
      });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="pt-36 pb-24 md:pt-44 md:pb-32 px-6 md:px-16" data-testid="contact-page">
      <div className="max-w-6xl mx-auto">
        <Eyebrow className="mb-6">Contact</Eyebrow>
        <LineReveal lines={["Say hello."]} className="display-1 text-cream mb-4" />
        <Reveal delay={0.3}>
          <p className="text-sage max-w-xl leading-relaxed text-sm md:text-base">
            Questions about a stay, or planning something special? Send an inquiry and we'll come
            back to you. For instant booking, our live booking engine is always open.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 mt-14">
          <div className="lg:col-span-7">
            {status === "sent" ? (
              <div className="border border-lavender/40 bg-lavender/[0.06] rounded-2xl p-10 text-center" data-testid="contact-success-message">
                <p className="font-display text-3xl text-cream">Thank you.</p>
                <p className="text-sage text-sm mt-3 leading-relaxed">
                  Your inquiry is on its way — we'll reply as soon as we're back from the field.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="contact-form">
                <input required placeholder="Name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                <select value={form.guests} onChange={set("guests")} className={inputCls} data-testid="contact-guests-select">
                  {["1", "2", "3", "4", "5", "6"].map((g) => (
                    <option key={g} value={g} className="bg-ink text-cream">{g} guest{g !== "1" ? "s" : ""}</option>
                  ))}
                </select>
                <input type="date" value={form.checkin} onChange={set("checkin")} className={inputCls} data-testid="contact-checkin-input" />
                <input type="date" value={form.checkout} onChange={set("checkout")} className={inputCls} data-testid="contact-checkout-input" />
                <select value={form.dome} onChange={set("dome")} className={`col-span-2 ${inputCls}`} data-testid="contact-dome-select">
                  <option value="" className="bg-ink text-cream">No preference</option>
                  <option value="Piwakawaka — Mountain View Dome with Private Hot Tub" className="bg-ink text-cream">Piwakawaka — Mountain View Dome with Private Hot Tub</option>
                  <option value="Kārearea — Luxury Dome with Hot Tub" className="bg-ink text-cream">Kārearea — Luxury Dome with Hot Tub</option>
                  <option value="Wedding or event enquiry" className="bg-ink text-cream">Wedding or event enquiry</option>
                </select>
                <textarea placeholder="Your message" rows={5} value={form.message} onChange={set("message")} className={`col-span-2 ${inputCls}`} data-testid="contact-message-input" />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary col-span-2 justify-center disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {status === "sending" ? "Sending…" : "Send inquiry"}
                </button>
                {status === "error" && (
                  <p className="col-span-2 text-center text-sm text-amber2" data-testid="contact-error-message">
                    Something went wrong sending your inquiry — please try again, or book directly
                    through the live booking engine.
                  </p>
                )}
              </form>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-white/10 rounded-2xl p-8 bg-moss/60">
                <p className="font-mono text-[0.62rem] uppercase tracking-widest2 text-lavender mb-4">Find us</p>
                <p className="text-sage text-sm leading-relaxed">{BRAND.address}</p>
                <p className="font-mono text-[0.62rem] uppercase tracking-widest2 text-lavender mb-4 mt-8">Book direct</p>
                <p className="text-sage text-sm leading-relaxed">
                  Our online booking widget is being connected right now — check back soon for
                  instant booking on this site. Until then, the inquiry form on this page reaches
                  us fastest and we'll confirm your dates personally.
                </p>
                <p className="font-mono text-[0.62rem] uppercase tracking-widest2 text-lavender mb-4 mt-8">Getting here</p>
                <p className="text-fern text-xs leading-relaxed">
                  Remote enough to feel like a true escape — follow Danseys Pass Road and let the
                  map run out of town. Check road conditions over the pass in winter.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
